import { getMultiAppParamsFromLocation } from './getMultiAppParamsFromLocation';
import { clearMultiAppFromLocation } from './clearMultiAppFromLocation';

/**
 * Receiving-app multiApp handoff: read `?multiApp=`, exchange session, clear URL.
 *
 * Auth-mode agnostic — use from apps that are:
 * - OIDC / enterprise SSO only
 * - Classic username+password only
 * - Both
 *
 * Pass `exchangeSession` (preferred). It should:
 * 1. Call commons `loginSso(multiApp, poolName)` for this app's pool
 * 2. Persist returned classic credentials the way the app already does
 *    (Redux AUTH_LOGIN_SUCCESS, localForage, etc.)
 * 3. Resolve with a shape that includes `accessToken` on success
 *
 * `ssoLogin` is accepted as an alias of `exchangeSession` for older call sites.
 *
 * @param {object} params
 * @param {string} [params.search]
 * @param {function} [params.exchangeSession] - (multiApp) => Promise<{ accessToken?: string }|null>
 * @param {function} [params.ssoLogin] - alias for exchangeSession
 * @param {{ replace: function }} [params.history]
 * @param {{ pathname?: string, search?: string, hash?: string }} [params.location]
 * @returns {Promise<boolean>} true when a session was established
 */
export const tryMultiAppAutologin = async ({
  search,
  exchangeSession,
  ssoLogin,
  history,
  location
} = {}) => {
  const handleSession = exchangeSession || ssoLogin;
  const { multiApp } = getMultiAppParamsFromLocation(search);
  if (!multiApp) {
    return false;
  }

  if (typeof handleSession !== 'function') {
    return false;
  }

  const response = await handleSession(multiApp);
  const loggedIn = Boolean(response?.accessToken);

  if (loggedIn && history?.replace && location) {
    history.replace(clearMultiAppFromLocation(location));
  }

  return loggedIn;
};
